import React, {
  useState,
  useEffect,
  ChangeEvent,
  FocusEvent,
  useRef,
} from 'react';
import { FormControl, ListGroup, Form } from 'react-bootstrap';
import { SuggestionsType } from '../utils/Types';
import '../assets/style/MAutoCompleteMultiSelect.css';
import classNames from 'classnames';

interface AutocompleteProps {
  suggestions: SuggestionsType[];
  placeholder?: string;
  value: SuggestionsType[];
  onChange: (value: any) => void;
  noSuggestionsText?: string;
  maxSelectable?: number;
}

const MAutoCompleteMultiSelect: React.FC<AutocompleteProps> = ({
  suggestions,
  placeholder = 'Search',
  value,
  onChange,
  noSuggestionsText = 'No Options.',
  maxSelectable,
}) => {
  const [query, setQuery] = useState('');
  const [filteredSuggestions, setFilteredSuggestions] =
    useState<SuggestionsType[]>(suggestions);
  const [isFocused, setIsFocused] = useState(false);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setFilteredSuggestions(suggestions);
  }, [suggestions]);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsFocused(false);
      if (inputRef.current) {
        inputRef.current.blur();
      }
    }
  };

  useEffect(() => {
    const inputElement = inputRef.current;
    if (inputElement) {
      inputElement.addEventListener('keydown', handleKeyDown);
      return () => {
        inputElement.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, []);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const userInput = e.target.value;
    setQuery(userInput);

    const filtered = suggestions.filter(
      (suggestion) =>
        String(suggestion.label)
          .toLowerCase()
          .indexOf(userInput.toLowerCase()) > -1
    );

    setFilteredSuggestions(filtered);
  };

  const handleCheckboxChange = (suggestion: SuggestionsType) => {
    onChange(() =>
      value.some((item) => item.value === suggestion.value)
        ? value.filter((item) => item.value !== suggestion.value)
        : [...value, suggestion]
    );
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = (e: FocusEvent<HTMLButtonElement>) => {
    if (
      suggestionsRef.current &&
      !suggestionsRef.current.contains(e.relatedTarget as Node)
    ) {
      setIsFocused(false);
    }
  };

  return (
    <button
      onBlur={handleBlur}
      onFocus={handleFocus}
      onKeyDown={() => {}}
      tabIndex={-1}
      className="autocomplete-container button-container"
    >
      <FormControl
        type="text"
        placeholder={placeholder}
        value={query}
        onChange={handleChange}
        onFocus={handleFocus}
        className="autocomplete-input"
        ref={inputRef}
      />
      {isFocused && (
        <div
          ref={suggestionsRef}
          tabIndex={-1}
          className="autocomplete-suggestions-container"
        >
          <ListGroup className=" mt-2 autocomplete-suggestions">
            {filteredSuggestions.length ? (
              filteredSuggestions.map((suggestion: SuggestionsType) => (
                <ListGroup.Item
                  key={suggestion.value}
                  onClick={() => handleCheckboxChange(suggestion)}
                  className={classNames({
                    disabled:
                      (maxSelectable &&
                        value.length >= maxSelectable &&
                        !value.some(
                          (item) => item.value === suggestion.value
                        )) ||
                      suggestion.disable,
                  })}
                >
                  <Form.Check
                    type="checkbox"
                    label={`${suggestion.label} (${suggestion.id})`}
                    checked={value.some(
                      (item) => item.value === suggestion.value
                    )}
                    readOnly
                  />
                </ListGroup.Item>
              ))
            ) : (
              <ListGroup.Item>{noSuggestionsText}</ListGroup.Item>
            )}
          </ListGroup>
        </div>
      )}
    </button>
  );
};

export default MAutoCompleteMultiSelect;
